import { useCallback, useLayoutEffect } from 'react';
import { getLocalDateKey } from './dateUtils';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useTimelineLayout = (
    state: PlannerState, refs: PlannerRefs, updateActiveTimelineLabel: () => void
) => {
    const { setTimelineWindow, timelineScale, timelineWindow } = state;
    const {
        isProgrammaticTimelineNavigationRef, pendingPrependAnchorRef, pendingPrependHeightRef,
        pendingTimelineCurrentScrollRef, pendingTimelineCurrentSmoothScrollRef,
        pendingTimelineDaysViewDateRef, pendingTimelineTaskIdScrollRef,
        pendingTimelineTaskScrollAttemptsRef, pendingTimelineTaskScrollRef,
        pendingTimelineZoomTargetRef, timelineRef, timelineScrollLockRef,
    } = refs;
    const scrollToCurrentTime = useCallback(() => {
        const container = timelineRef.current;
        if (!container || timelineScale !== 'day') return false;
        const now = new Date();
        const slot = container.querySelector(
            `.timeline-slot[data-timeline-date="${getLocalDateKey(now)}"][data-timeline-hour="${now.getHours().toString().padStart(2, '0')}"]`
        ) as HTMLElement | null;
        if (!slot) return false;
        const containerRect = container.getBoundingClientRect();
        const slotRect = slot.getBoundingClientRect();
        const delta = slotRect.top - containerRect.top + now.getMinutes() / 60 * slot.offsetHeight - container.clientHeight / 2;
        const top = Math.max(0, container.scrollTop + delta);
        if (pendingTimelineCurrentSmoothScrollRef.current) container.scrollTo({ top, behavior: 'smooth' });
        else container.scrollTop = top;
        return true;
    }, [pendingTimelineCurrentSmoothScrollRef, timelineRef, timelineScale]);
    useLayoutEffect(() => {
        const container = timelineRef.current;
        const zoom = timelineScale === 'month' ? null : pendingTimelineZoomTargetRef.current;
        if (timelineScale === 'month') {
            pendingTimelineZoomTargetRef.current = null;
            pendingTimelineDaysViewDateRef.current = null;
        }
        if (container && zoom && timelineScale === zoom.scale) {
            const date = new Date(zoom.date);
            if (zoom.scale === 'year' || zoom.scale === 'years') {
                date.setMonth(0, 1);
                date.setHours(0, 0, 0, 0);
            }
            const selector = zoom.scale === 'year'
                ? `.timeline-year-calendar[data-timeline-year-date="${getLocalDateKey(date)}"]`
                : zoom.scale === 'years'
                    ? `.timeline-years-calendar[data-timeline-years-date="${date.toISOString()}"]`
                    : `.timeline-slot[data-timeline-row-date="${getLocalDateKey(date)}"]`;
            const scrollToTarget = () => {
                const target = container.querySelector(selector) as HTMLElement | null;
                if (!target) return false;
                const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
                container.scrollTop = Math.max(0, container.scrollTop + offset - 4);
                return true;
            };
            const settleZoom = () => {
                if (!scrollToTarget()) {
                    window.setTimeout(() => setTimelineWindow((range) => ({ ...range })), 0);
                    return;
                }
                pendingTimelineZoomTargetRef.current = null;
                window.setTimeout(() => { isProgrammaticTimelineNavigationRef.current = false; }, 80);
            };
            isProgrammaticTimelineNavigationRef.current = true;
            scrollToTarget();
            window.requestAnimationFrame(settleZoom);
        }
        const daysDate = pendingTimelineDaysViewDateRef.current;
        if (container && daysDate && timelineScale === 'month') {
            const target = container.querySelector(
                `.timeline-slot[data-timeline-row-date="${getLocalDateKey(daysDate)}"]`
            ) as HTMLElement | null;
            if (target) {
                container.scrollTop = Math.max(0, target.offsetTop - 4);
                pendingTimelineDaysViewDateRef.current = null;
            }
        }
        const taskDate = pendingTimelineTaskScrollRef.current;
        if (taskDate) {
            const id = pendingTimelineTaskIdScrollRef.current;
            const taskSelector = id ? `[data-timeline-task-id="${id}"]` : null;
            const task = taskSelector
                ? container?.querySelector(`.timeline-period ${taskSelector}`) as HTMLElement | null : null;
            const calendarDay = container?.querySelector(
                `[data-calendar-day="${getLocalDateKey(taskDate)}"]`
            ) as HTMLElement | null;
            const slot = container?.querySelector(
                `.timeline-slot[data-timeline-date="${getLocalDateKey(taskDate)}"][data-timeline-hour="${taskDate.getHours().toString().padStart(2, '0')}"]`
            ) as HTMLElement | null;
            if (container && (task || calendarDay || slot)) {
                window.requestAnimationFrame(() => {
                    const target = task || calendarDay || slot;
                    if (!target) return;
                    const containerRect = container.getBoundingClientRect();
                    const targetRect = target.getBoundingClientRect();
                    const header = container.querySelector('.timeline-zoom-controls') as HTMLElement | null;
                    const headerHeight = header?.offsetHeight || 0;
                    const top = container.scrollTop + targetRect.top - containerRect.top
                        - headerHeight;
                    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
                });
                pendingTimelineTaskScrollRef.current = null;
                pendingTimelineTaskIdScrollRef.current = null;
                pendingTimelineTaskScrollAttemptsRef.current = 0;
                pendingTimelineCurrentScrollRef.current = false;
                isProgrammaticTimelineNavigationRef.current = true;
                window.setTimeout(() => {
                    isProgrammaticTimelineNavigationRef.current = false;
                }, 650);
                return;
            }
            pendingTimelineTaskScrollAttemptsRef.current += 1;
            if (pendingTimelineTaskScrollAttemptsRef.current > 10) {
                pendingTimelineTaskScrollRef.current = null;
                pendingTimelineTaskIdScrollRef.current = null;
                pendingTimelineTaskScrollAttemptsRef.current = 0;
                isProgrammaticTimelineNavigationRef.current = false;
            } else window.setTimeout(() => setTimelineWindow((range) => ({ ...range })), 30);
            pendingTimelineCurrentScrollRef.current = false;
            return;
        }
        if (pendingTimelineCurrentScrollRef.current) {
            const didScroll = scrollToCurrentTime();
            const currentSelector = timelineScale === 'month'
                ? '[data-calendar-current="true"]' : '[data-timeline-current-period="true"]';
            const current = container?.querySelector(currentSelector) as HTMLElement | null;
            if (!didScroll && container && current) {
                const top = Math.max(0, current.offsetTop - 48);
                if (pendingTimelineCurrentSmoothScrollRef.current) container.scrollTo({ top, behavior: 'smooth' });
                else container.scrollTop = top;
            }
            window.setTimeout(updateActiveTimelineLabel, 0);
            pendingTimelineCurrentScrollRef.current = false;
            pendingTimelineCurrentSmoothScrollRef.current = false;
            return;
        }
        if (!timelineScrollLockRef.current) return;
        if (container) {
            const anchor = pendingPrependAnchorRef.current;
            const period = anchor ? container.querySelector(
                `.timeline-period[data-timeline-period-key="${anchor.key}"]`
            ) as HTMLElement | null : null;
            if (anchor && period) {
                container.scrollTop += period.getBoundingClientRect().top
                    - container.getBoundingClientRect().top - anchor.top;
            } else if (pendingPrependHeightRef.current !== null) {
                container.scrollTop += container.scrollHeight - pendingPrependHeightRef.current;
            }
        }
        pendingPrependHeightRef.current = null;
        pendingPrependAnchorRef.current = null;
        window.setTimeout(updateActiveTimelineLabel, 0);
        window.setTimeout(() => { timelineScrollLockRef.current = false; }, 80);
    }, [isProgrammaticTimelineNavigationRef, pendingPrependAnchorRef, pendingPrependHeightRef,
        pendingTimelineCurrentScrollRef, pendingTimelineCurrentSmoothScrollRef,
        pendingTimelineDaysViewDateRef, pendingTimelineTaskIdScrollRef,
        pendingTimelineTaskScrollAttemptsRef, pendingTimelineTaskScrollRef,
        pendingTimelineZoomTargetRef, scrollToCurrentTime, setTimelineWindow, timelineRef,
        timelineScale, timelineScrollLockRef, timelineWindow, updateActiveTimelineLabel]);
};
